import React, { useState } from "react";
import { api, extractError } from "../../lib/api";
import { KpiTile, SectionCard } from "../Ui";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "../ui/dialog";
import { toast } from "sonner";

const PACK_SIZES = ["10", "25", "30", "50"];
const REASON_PILLS = ["Defective Stitching", "Tally Shortage", "Bag Tear"];

function bags(n) {
  return Number(n || 0).toLocaleString("en-IN");
}

function todayIso() {
  return new Date().toISOString().slice(0, 10);
}

const emptyClaim = () => ({
  work_date: todayIso(),
  shift: "DAY",
  contractor_id: "",
  production_batch_id: "",
  item_id: "",
  pack_size_kg: "50",
  claimed_bags_count: "",
});

export default function LabourVerificationQueue({ data, contractors, onReload }) {
  const kpis = data?.kpis || {};
  const claims = data?.pending_claims || [];
  const [form, setForm] = useState(emptyClaim);
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(null);

  const submitClaim = async (event) => {
    event.preventDefault();
    setBusy(true);
    try {
      await api.post("/labour/packing-claims/submit", {
        work_date: form.work_date,
        shift: form.shift,
        contractor_id: Number(form.contractor_id),
        production_batch_id: Number(form.production_batch_id),
        item_id: Number(form.item_id),
        pack_size_kg: Number(form.pack_size_kg),
        claimed_bags_count: Number(form.claimed_bags_count),
      });
      toast.success("Claim is waiting for supervisor verification. Stock and billing are unchanged.");
      setForm(emptyClaim());
      await onReload();
    } catch (err) {
      toast.error(extractError(err));
    } finally {
      setBusy(false);
    }
  };

  const openClaim = (claim, mode) => {
    setDraft({
      id: claim.id,
      claimed: Number(claim.claimed_bags_count || 0),
      title: `${claim.contractor_name || "Contractor"} · ${claim.item_name || "Item"} · ${claim.pack_size_kg} kg`,
      approved: String(claim.claimed_bags_count || ""),
      reason: "",
      destination: "STACKED",
      stackId: "",
      lot: "",
      mode,
    });
    setOpen(true);
  };

  const addReason = (pill) => {
    setDraft((d) => {
      if (!d) return d;
      const current = d.reason.trim();
      if (current.toLowerCase().includes(pill.toLowerCase())) return d;
      return { ...d, reason: current ? `${current}; ${pill}` : pill };
    });
  };

  const verify = async (reject) => {
    if (!draft) return;
    const approved = Number(draft.approved);
    if (!reject && (Number.isNaN(approved) || approved < 0 || approved > draft.claimed)) {
      toast.error("Approved bags cannot exceed the claimed count.");
      return;
    }
    if (!reject && approved < draft.claimed && !draft.reason.trim()) {
      toast.error("A discrepancy reason is required when the approved count is below the claim.");
      return;
    }
    if (reject && !draft.reason.trim()) {
      toast.error("A rejection reason is required.");
      return;
    }
    if (!reject && draft.destination === "STACKED" && !draft.stackId) {
      toast.error("Choose the stack these bags go into.");
      return;
    }
    setBusy(true);
    try {
      await api.post(`/labour/packing-claims/${draft.id}/verify`, {
        approved_bags_count: reject ? 0 : approved,
        discrepancy_reason: draft.reason.trim(),
        reject,
        destination_type: draft.destination,
        target_stack_id: draft.destination === "STACKED" && draft.stackId ? Number(draft.stackId) : null,
        lot_number: draft.lot,
      });
      toast.success(reject ? "Claim rejected. No stock or billing was posted." : "Verified count posted to stock and the contractor’s earnings.");
      setOpen(false);
      setDraft(null);
      await onReload();
    } catch (err) {
      toast.error(extractError(err));
    } finally {
      setBusy(false);
    }
  };

  const partial = draft && !Number.isNaN(Number(draft.approved)) && Number(draft.approved) < draft.claimed;

  return (
    <div className="space-y-4">
      <div className="grid gap-3 sm:grid-cols-3">
        <KpiTile label="Pending claims" value={bags(kpis.pending_claims)} accent testId="kpi-pending-claims" />
        <KpiTile label="Verified today (bags)" value={bags(kpis.verified_today_bags)} testId="kpi-verified-today" />
        <KpiTile label="Discrepancy / variance bags" value={bags(kpis.discrepancy_bags)} testId="kpi-discrepancy" />
      </div>

      <SectionCard title="Supervisor verification queue" subtitle="A claim stays out of inventory and billing until you approve a count.">
        {claims.length === 0 ? (
          <p className="text-sm text-muted-foreground">No packing claims are waiting.</p>
        ) : (
          <div className="grid gap-3 md:grid-cols-2">
            {claims.map((claim) => (
              <article key={claim.id} className="rounded-lg border border-border bg-card p-4 space-y-3">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="text-sm font-semibold text-foreground">{claim.contractor_name || "Contractor"}</p>
                    <p className="text-xs text-muted-foreground mt-1">
                      {claim.work_date} · {claim.shift} · Batch {claim.production_batch_code || claim.production_batch_id}
                    </p>
                  </div>
                  <span className="text-[10px] uppercase tracking-wide rounded-full border border-border px-2 py-1 text-muted-foreground">
                    Pending
                  </span>
                </div>
                <p className="text-sm text-foreground">
                  {claim.item_name || "Variety"} · {claim.pack_size_kg} kg · claimed {bags(claim.claimed_bags_count)} bags
                </p>
                <div className="flex flex-wrap gap-2">
                  <Button type="button" onClick={() => openClaim(claim, "approve")}>Approve count</Button>
                  <Button type="button" variant="outline" onClick={() => openClaim(claim, "reject")}>Deny / reject</Button>
                </div>
              </article>
            ))}
          </div>
        )}
      </SectionCard>

      <SectionCard title="Log a packing claim" subtitle="This records the maker’s count only. Finished stock does not increase yet.">
        <form className="grid gap-3 md:grid-cols-2" onSubmit={submitClaim}>
          <div className="space-y-1.5">
            <Label>Date</Label>
            <Input type="date" value={form.work_date} onChange={(e) => setForm((f) => ({ ...f, work_date: e.target.value }))} required />
          </div>
          <div className="space-y-1.5">
            <Label>Shift</Label>
            <Select value={form.shift} onValueChange={(v) => setForm((f) => ({ ...f, shift: v }))}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="DAY">Day</SelectItem>
                <SelectItem value="NIGHT">Night</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>Packing contractor</Label>
            <Select value={form.contractor_id || undefined} onValueChange={(v) => setForm((f) => ({ ...f, contractor_id: v }))}>
              <SelectTrigger><SelectValue placeholder="Select contractor" /></SelectTrigger>
              <SelectContent>
                {contractors.map((p) => (
                  <SelectItem key={p.id} value={String(p.id)}>{p.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>Production batch</Label>
            <Select value={form.production_batch_id || undefined} onValueChange={(v) => setForm((f) => ({ ...f, production_batch_id: v }))}>
              <SelectTrigger><SelectValue placeholder="Select batch" /></SelectTrigger>
              <SelectContent>
                {(data?.batches || []).map((b) => (
                  <SelectItem key={b.id} value={String(b.id)}>{b.code}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>Variety</Label>
            <Select value={form.item_id || undefined} onValueChange={(v) => setForm((f) => ({ ...f, item_id: v }))}>
              <SelectTrigger><SelectValue placeholder="Select item" /></SelectTrigger>
              <SelectContent>
                {(data?.items || []).map((item) => (
                  <SelectItem key={item.id} value={String(item.id)}>{item.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>Bag size (kg)</Label>
            <Select value={form.pack_size_kg} onValueChange={(v) => setForm((f) => ({ ...f, pack_size_kg: v }))}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {PACK_SIZES.map((size) => (
                  <SelectItem key={size} value={size}>{size} kg</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>Claimed bags</Label>
            <Input
              type="number"
              min="1"
              value={form.claimed_bags_count}
              onChange={(e) => setForm((f) => ({ ...f, claimed_bags_count: e.target.value }))}
              required
            />
          </div>
          <div className="md:col-span-2">
            <Button type="submit" disabled={busy}>Submit claim</Button>
          </div>
        </form>
      </SectionCard>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-card text-foreground border-border max-w-lg">
          <DialogHeader>
            <DialogTitle>{draft?.mode === "reject" ? "Deny packing claim" : "Verify packed bags"}</DialogTitle>
          </DialogHeader>
          {draft && (
            <div className="space-y-3">
              <p className="text-sm text-muted-foreground">{draft.title}</p>
              <p className="text-sm text-foreground">Claimed {bags(draft.claimed)} bags</p>
              {draft.mode === "approve" && (
                <>
                  <div className="space-y-1.5">
                    <Label>Approved bags count</Label>
                    <Input
                      type="number"
                      min="0"
                      max={draft.claimed}
                      value={draft.approved}
                      onChange={(e) => setDraft((d) => ({ ...d, approved: e.target.value }))}
                    />
                  </div>
                  {partial && (
                    <div className="rounded-md border border-border bg-muted px-3 py-2 text-sm text-foreground">
                      Approved count is below the claim. Stock and billing will use only the approved bags. A discrepancy reason is required.
                    </div>
                  )}
                  <div className="space-y-1.5">
                    <Label>Destination</Label>
                    <Select value={draft.destination} onValueChange={(v) => setDraft((d) => ({ ...d, destination: v }))}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="STACKED">Stack #</SelectItem>
                        <SelectItem value="DIRECT_LOADED">Direct truck loading</SelectItem>
                        <SelectItem value="FLOOR_HOLD">Floor / transit</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {draft.destination === "STACKED" && (
                    <div className="space-y-1.5">
                      <Label>Stack</Label>
                      <Select value={draft.stackId || undefined} onValueChange={(v) => setDraft((d) => ({ ...d, stackId: v }))}>
                        <SelectTrigger><SelectValue placeholder="Select stack" /></SelectTrigger>
                        <SelectContent>
                          {(data?.stacks || []).map((stack) => (
                            <SelectItem key={stack.id} value={String(stack.id)}>
                              {stack.godown_name} · {stack.stack_number}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                </>
              )}
              <div className="space-y-1.5">
                <Label>{draft.mode === "reject" ? "Rejection reason" : "Discrepancy reason"}</Label>
                <div className="flex flex-wrap gap-2">
                  {REASON_PILLS.map((pill) => (
                    <Button key={pill} type="button" size="sm" variant="outline" onClick={() => addReason(pill)}>
                      {pill}
                    </Button>
                  ))}
                </div>
                <Textarea
                  value={draft.reason}
                  onChange={(e) => setDraft((d) => ({ ...d, reason: e.target.value }))}
                  placeholder={draft.mode === "reject" ? "Why is this claim denied?" : "Required when the approved count is lower"}
                />
              </div>
            </div>
          )}
          <DialogFooter>
            {draft?.mode === "reject" ? (
              <Button type="button" variant="destructive" disabled={busy} onClick={() => verify(true)}>Deny / reject</Button>
            ) : (
              <Button type="button" disabled={busy} onClick={() => verify(false)}>Approve count</Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
